import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { PhotoHero, PhotoProcess, WorkGallery, FaqSection } from "@/components/sections/service-photo";
import { Fan, Flame, PipetteIcon, Gauge, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";

export default function HVAC() {
  const services = [
    {
      icon: <Fan className="w-8 h-8" />,
      title: "Ventilation Systems",
      desc: "Complete ventilation design, supply and installation. MVHR, MEV, extract fans, and ducted systems for residential and commercial properties.",
      color: "text-blue-600 bg-blue-50",
      href: "/hvac/ventilation"
    },
    {
      icon: <Flame className="w-8 h-8" />,
      title: "Heating Systems",
      desc: "Energy-efficient heating solutions including heat pumps, underfloor heating, and commercial heating systems designed for optimal comfort.",
      color: "text-orange-600 bg-orange-50",
      href: "/hvac/heating"
    },
    {
      icon: <PipetteIcon className="w-8 h-8" />,
      title: "Ductwork",
      desc: "Custom fabrication, installation and cleaning of sheet metal and flexible ductwork for ventilation, heating and cooling distribution.",
      color: "text-emerald-600 bg-emerald-50",
      href: "/hvac/ductwork"
    },
    {
      icon: <Gauge className="w-8 h-8" />,
      title: "BMS & Controls",
      desc: "Building Management Systems for intelligent control of your HVAC infrastructure. Optimise energy usage and maintain perfect conditions.",
      color: "text-purple-600 bg-purple-50",
      href: "/hvac/controls"
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />

      <main className="flex-1">
        <PhotoHero src="ventilation-installation.webp" alt={"Installed exposed ventilation ductwork and ceiling cassette in a commercial fit-out"} position="center" overlay="bg-gradient-to-r from-slate-950/90 via-slate-900/70 to-slate-900/25" eyebrow="VertexAir HVAC" testId="section-hero">
              <h1 className="text-4xl md:text-5xl font-bold font-display mb-4">HVAC Systems</h1>
              <p className="text-lg md:text-xl text-primary-foreground/80 mb-6">
                Complete heating, ventilation and air conditioning solutions. From design through to installation, commissioning and ongoing maintenance.
              </p>
              <Link href="/quote">
                <Button size="lg" variant="secondary" className="rounded-full h-14 px-8 text-lg font-bold">
                  Request a Free Survey
                </Button>
              </Link>
        </PhotoHero>

        <section className="py-12 lg:py-16 bg-background">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">Our HVAC Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">End-to-end HVAC solutions tailored to your building's specific requirements.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, i) => (
                <Link key={i} href={service.href}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="group bg-card p-8 rounded-2xl shadow-sm border border-border hover:shadow-xl hover:shadow-primary/5 transition-all cursor-pointer h-full"
                  >
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 ${service.color} group-hover:scale-110 transition-transform`}>
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold font-display mb-3 group-hover:text-primary transition-colors">{service.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">{service.desc}</p>
                    <span className="text-sm font-semibold text-primary">Learn more &rarr;</span>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-16 bg-slate-50 border-y border-border">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold font-display mb-6">Complete HVAC Lifecycle Support</h2>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  From initial design consultation through to ongoing preventative maintenance, we support your HVAC systems throughout their entire lifecycle.
                </p>
                <ul className="space-y-4">
                  {[
                    "System design and engineering",
                    "Supply and installation",
                    "Commissioning and handover",
                    "Planned preventative maintenance",
                    "Emergency breakdown response",
                    "Energy efficiency audits"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl">
                <img src="/images/commercial.webp" alt="HVAC Systems" className="w-full h-full object-cover" />
                <div className="absolute inset-0 ring-1 ring-inset ring-black/10 rounded-2xl"></div>
              </div>
            </div>
          </div>
        </section>

        <PhotoProcess title={"How an HVAC project runs"} intro={"Each project follows the same practical sequence, whether it is one extract fan or a full plant replacement."} steps={[{ title: "Survey", desc: "We look at the building, existing plant, access, power supplies and how the space is used before recommending anything." }, { title: "Design and proposal", desc: "Equipment selection, duct and pipe routes, controls and builders' work are set out so you can see what is included." }, { title: "Installation", desc: "Plant, ductwork, pipework and controls are installed to the agreed drawings, with areas kept tidy and safe while work is live." }, { title: "Commissioning", desc: "Airflows, temperatures and control settings are checked and adjusted against the design before handover." }, { title: "Handover and maintenance", desc: "You receive operating guidance and records, with the option of planned maintenance visits afterwards." }]} photo="project-8.webp" alt={"Ducted HVAC plant unit installed on site"} caption={"Installed ducted plant unit."} accent="bg-primary" testId="section-process" />
        <WorkGallery title={"Recent HVAC work"} intro={"Photos from VertexAir sites. Captions describe what is visible; work-in-progress images are labelled."} items={[{ src: "ventilation-installation.webp", alt: "Exposed ventilation ducts and cassette in a commercial space", caption: "Installed exposed ventilation ducts and ceiling cassette in a commercial fit-out." }, { src: "heating-daikin.webp", alt: "Daikin outdoor units installed", caption: "Installed Daikin outdoor equipment." }, { src: "bms-controls.webp", alt: "Four wall-mounted controllers", caption: "Four installed wall controllers." }, { src: "ductwork-installation.webp", alt: "Galvanised ductwork at high level", caption: "Installed exposed galvanised ductwork, with other trades still working below." }, { src: "project-6.webp", alt: "Electrical controls cabinet", caption: "Electrical controls cabinet for HVAC equipment." }, { src: "project-21.webp", alt: "Rooftop HVAC equipment", caption: "Rooftop HVAC equipment." }, { src: "project-17.webp", alt: "Engineer servicing a filter", caption: "Engineer servicing a filter during maintenance." }, { src: "home-commercial-installation.webp", alt: "Unfinished commercial ceiling installation", caption: "Work in progress: commercial ceiling installation before finishes." }]} testId="section-gallery" />
        <FaqSection title={"HVAC questions"} intro={"Common questions from building owners, managers and homeowners."} faqs={[{ q: "Which HVAC service do I need?", a: "If the problem is stale air, condensation or odours, start with ventilation. Cold or uneven rooms point to heating. Noisy or weak airflow usually involves ductwork, and systems that run at the wrong times often need controls attention. A survey lets us check before you commit." }, { q: "Do you work on existing systems as well as new installs?", a: "Yes. We service, repair, extend and upgrade existing equipment, and we will tell you plainly when replacement makes more sense than repair." }, { q: "What happens at a survey?", a: "An engineer looks at the space, existing plant, access routes, electrical supplies and where equipment could go, and asks how the building is used. You then receive a written proposal." }, { q: "Can work be done while the building stays open?", a: "Often, yes. We can phase work by area and agree noisy or disruptive tasks for quieter times. This is planned with you at quotation stage." }, { q: "What do I receive at handover?", a: "Commissioning records for the work carried out, operating guidance for users and any manufacturer documents supplied with the equipment." }, { q: "Do you offer maintenance after installation?", a: "Yes. Planned maintenance visits can cover filters, coils, drains, fans, controls and general condition checks, at a frequency suited to the system and building use." }]} testId="section-faq" />
      </main>

      <Footer />
    </div>
  );
}
